import { Flex, Group, Menu, Tabs, Title } from "@mantine/core";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { AddPaymentModal } from "@/components/features/AccountsReceivable/AddPaymentModal";
import { AgingReportTab } from "@/components/features/AccountsReceivable/AgingReportTab";
import { PaymentsTab } from "@/components/features/AccountsReceivable/PaymentsTab";
import { PlaceholderTab } from "@/components/features/AccountsReceivable/PlaceholderTab";
import { ReadyToInvoiceTab } from "@/components/features/AccountsReceivable/ReadyToInvoiceTab";
import { SentTab } from "@/components/features/AccountsReceivable/SentTab";
import { Button } from "@/components/ui/Button/Button";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useRequestScope } from "@/hooks/useRequestScope";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import type { TabItemType } from "@/types/common.types";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

import styles from "./accounts-receivable.module.scss";

const accountsReceivableSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/accounts-receivable/")({
  component: AccountsReceivable,
  validateSearch: accountsReceivableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function AccountsReceivable() {
  const searchParams = useSearch({ from: "/_authenticated/accounts-receivable/" });
  const requestScope = useRequestScope();
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);

  useBreadcrumb(
    [
      {
        title: "Accounts Receivable",
        href: "/accounts-receivable",
        icon: getRouteIcon(MODULE_KEYS_MAP.accountsReceivable),
      },
    ],
    { listView: "List View" },
  );

  const { hasAccess } = usePermissions();
  const { create: canCreatePayment } = hasAccess({
    module: MODULE_KEYS_MAP.accountsReceivable,
  });

  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "readyToInvoice" });

  const tabsData: TabItemType[] = [
    {
      value: "readyToInvoice",
      label: "Ready to invoice",
      component: (
        <ReadyToInvoiceTab isActive={activeTab === "readyToInvoice"} searchParams={searchParams} />
      ),
    },
    {
      value: "sent",
      label: "Sent",
      component: <SentTab isActive={activeTab === "sent"} searchParams={searchParams} />,
    },
    {
      value: "payments",
      label: "Payments",
      component: <PaymentsTab isActive={activeTab === "payments"} searchParams={searchParams} />,
    },
    {
      value: "agingReport",
      label: "Aging report",
      component: <AgingReportTab isActive={activeTab === "agingReport"} />,
    },
    {
      value: "creditReport",
      label: "Credit Report",
      component: <PlaceholderTab kind="credits" isActive={activeTab === "creditReport"} />,
    },
    {
      value: "discounts",
      label: "Discounts",
      component: <PlaceholderTab kind="discounts" isActive={activeTab === "discounts"} />,
    },
  ];

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          Accounts Receivable
        </Title>
        {canCreatePayment && (
          <Group gap={2}>
            <Button
              variant="primary"
              style={{
                height: "var(--component-size-small)",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
              iconLeft={<IconPlus size={16} />}
              onClick={() => setIsAddPaymentOpen(true)}
            >
              Add Payment
            </Button>
            <Menu position="bottom-end">
              <Menu.Target>
                <Button
                  variant="primary"
                  aria-label="More payment actions"
                  style={{
                    height: "var(--component-size-small)",
                    paddingInline: 8,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  <IconChevronDown size={16} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => setIsAddPaymentOpen(true)}>Add Payment</Menu.Item>
                <Menu.Item onClick={() => setIsAddPaymentOpen(true)}>
                  Import remittance (OCR)
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        )}
      </Group>

      <Tabs
        key={requestScope}
        value={activeTab}
        onChange={(value) => setActiveTab(value || "readyToInvoice")}
        className={styles.tab}
      >
        <Tabs.List className={styles.tabsList}>
          {tabsData.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabsData.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>

      <AddPaymentModal isOpen={isAddPaymentOpen} onClose={() => setIsAddPaymentOpen(false)} />
    </Flex>
  );
}
